import { useEffect, useState, useCallback } from "react";
import { useAuth } from "@/context/AuthContext";
import { hasPerm } from "@/lib/permissions";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Tabs, TabsList, TabsTrigger, TabsContent,
} from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  CreditCard, CheckCircle2, XCircle, Eye, AlertTriangle, FileText, ShieldCheck, Mail, Clock, Inbox, RefreshCcw,
} from "lucide-react";
import { toast } from "sonner";
import ClearTestDataButton from "@/components/admin/ClearTestDataButton";

const STATUS_STYLES = {
  pending_verification: { color: "bg-amber-100 text-amber-800 border-amber-200", label: "Pending Verification" },
  verified: { color: "bg-blue-100 text-blue-800 border-blue-200", label: "Verified — Awaiting Confirmation" },
  confirmed: { color: "bg-emerald-100 text-emerald-800 border-emerald-200", label: "Confirmed (Full Access)" },
  rejected: { color: "bg-rose-100 text-rose-800 border-rose-200", label: "Rejected" },
};

export default function PaymentsPage() {
  const { user } = useAuth();
  const canApprove = hasPerm(user, "enrollment_request", "approve");
  const canReject = hasPerm(user, "enrollment_request", "reject");
  const [tab, setTab] = useState("pending_verification");
  const [data, setData] = useState({ payments: [], counts: {} });
  const [busyId, setBusyId] = useState(null);
  const [rejectDialog, setRejectDialog] = useState({ open: false, id: null, reason: "" });
  const [confirmDialog, setConfirmDialog] = useState({ open: false, id: null });
  const [proofDialog, setProofDialog] = useState({ open: false, loading: false, data: null, payment: null });

  const load = useCallback(async (status = tab) => {
    try {
      const r = await api.get("/admin/payments", { params: { status } });
      setData(r.data || { payments: [], counts: {} });
    } catch { toast.error("Could not load payments"); }
  }, [tab]);

  useEffect(() => { load(tab); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [tab]);

  const verify = async (id) => {
    setBusyId(id);
    try {
      const r = await api.post(`/admin/payments/${id}/verify`);
      toast.success(r?.data?.message || "Payment verified");
      await load(tab);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusyId(null); }
  };

  const reject = async () => {
    const { id, reason } = rejectDialog;
    setBusyId(id);
    try {
      await api.post(`/admin/payments/${id}/reject`, { reason });
      toast.success("Payment rejected");
      setRejectDialog({ open: false, id: null, reason: "" });
      await load(tab);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusyId(null); }
  };

  const confirm = async () => {
    const { id } = confirmDialog;
    setBusyId(id);
    try {
      await api.post(`/admin/payments/${id}/confirm-full-access`);
      toast.success("Payment has been permanently confirmed. Full course access has been granted.");
      setConfirmDialog({ open: false, id: null });
      await load(tab);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusyId(null); }
  };

  const viewProof = async (payment) => {
    setProofDialog({ open: true, loading: true, data: null, payment });
    try {
      const r = await api.get(`/admin/payments/${payment.id}`);
      setProofDialog({ open: true, loading: false, data: r.data, payment });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
      setProofDialog({ open: false, loading: false, data: null, payment: null });
    }
  };

  return (
    <div className="space-y-8" data-testid="payments-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-blue-600 flex items-center gap-2">
            <CreditCard className="w-3.5 h-3.5" /> Payments
          </div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mt-1">Student payment receipts</h1>
          <p className="text-slate-600 dark:text-slate-300 mt-1 max-w-2xl">
            Verify and permanently confirm payments to unlock full course access for approved students.
          </p>
        </div>
        <ClearTestDataButton
          role={user?.role}
          title="Clear Confirmed"
          description="Permanently deletes all payments with status Confirmed (Full Access). Pending / Verified / Rejected records are kept."
          endpoint="/admin/payments/clear-confirmed"
          confirmPhrase="CLEAR"
          onCleared={() => load(tab)}
          dataTestId="clear-payments-confirmed"
        />
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="pending_verification" data-testid="pay-tab-pending">
            Pending {data.counts.pending_verification ? <Badge className="ml-2 bg-amber-500 text-white">{data.counts.pending_verification}</Badge> : null}
          </TabsTrigger>
          <TabsTrigger value="verified" data-testid="pay-tab-verified">Verified ({data.counts.verified || 0})</TabsTrigger>
          <TabsTrigger value="confirmed" data-testid="pay-tab-confirmed">Confirmed ({data.counts.confirmed || 0})</TabsTrigger>
          <TabsTrigger value="rejected" data-testid="pay-tab-rejected">Rejected ({data.counts.rejected || 0})</TabsTrigger>
        </TabsList>

        {["pending_verification", "verified", "confirmed", "rejected"].map((s) => (
          <TabsContent key={s} value={s} className="mt-5">
            {(!data.payments || data.payments.length === 0) ? (
              <Card className="p-12 text-center" data-testid={`pay-empty-${s}`}>
                <Inbox className="w-10 h-10 text-slate-300 mx-auto mb-2" />
                <div className="text-sm text-slate-500">No {STATUS_STYLES[s].label.toLowerCase()} payments.</div>
              </Card>
            ) : (
              <Card className="p-0 border-slate-200 overflow-hidden">
                <ul className="divide-y divide-slate-100">
                  {data.payments.map((p) => {
                    const meta = STATUS_STYLES[p.payment_status] || STATUS_STYLES.pending_verification;
                    return (
                      <li key={p.id} className="p-5 flex items-start gap-4 flex-wrap" data-testid={`pay-row-${p.id}`}>
                        <div className="h-10 w-10 rounded-lg bg-blue-100 text-blue-700 grid place-items-center shrink-0">
                          <CreditCard className="w-5 h-5" strokeWidth={1.5} />
                        </div>
                        <div className="flex-1 min-w-[260px]">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-display font-bold text-base text-slate-900">{p.student_name}</span>
                            {p.is_renewal && (
                              <Badge className="bg-indigo-600 text-white border-indigo-600" data-testid={`pay-renewal-badge-${p.id}`}>
                                <RefreshCcw className="w-3 h-3 mr-1" /> Renewal
                              </Badge>
                            )}
                            <Badge variant="outline" className={meta.color + " border"} data-testid={`pay-badge-${p.id}`}>{meta.label}</Badge>
                          </div>
                          <div className="text-xs text-slate-500 flex items-center gap-1 mt-0.5">
                            <Mail className="w-3 h-3" /> {p.student_email}
                          </div>
                          {p.is_renewal && p.course_name && (
                            <div className="text-xs text-indigo-900 dark:text-indigo-200 mt-1 font-medium">
                              Course: <span className="font-normal">{p.course_name}</span>
                            </div>
                          )}
                          {p.payment_system_name && (
                            <div className="text-xs text-slate-700 mt-0.5">
                              Paid via: <span className="font-medium">{p.payment_system_name}</span>
                            </div>
                          )}
                          {typeof p.amount_paid === "number" && (
                            <div className="text-xs text-slate-700 mt-0.5">
                              Amount: <span className="font-mono font-semibold">₱{p.amount_paid.toFixed(2)}</span>
                            </div>
                          )}
                          {p.payment_reference_number && (
                            <div className="text-xs text-slate-700 mt-1">
                              Ref: <span className="font-mono">{p.payment_reference_number}</span>
                            </div>
                          )}
                          {p.notes && (
                            <div className="text-xs text-slate-600 mt-1 italic bg-slate-50 px-3 py-2 rounded border-l-2 border-blue-300">"{p.notes}"</div>
                          )}
                          {p.rejection_reason && (
                            <div className="text-xs text-rose-700 mt-1 flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> Rejected: {p.rejection_reason}</div>
                          )}
                          <div className="text-[11px] text-slate-400 mt-2 flex items-center gap-3 flex-wrap">
                            <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" /> Submitted {(p.submitted_at || "").slice(0, 19).replace("T", " ")}</span>
                            {p.confirmed_by_admin_name && <span>· Confirmed by {p.confirmed_by_admin_name}</span>}
                          </div>
                        </div>
                        <div className="flex gap-2 shrink-0 flex-wrap justify-end">
                          {(p.proof_of_payment_filename || p.payment_reference_number) && (
                            <Button variant="outline" size="sm" onClick={() => viewProof(p)} data-testid={`pay-view-proof-${p.id}`}>
                              <Eye className="w-4 h-4 mr-1" /> View Receipt
                            </Button>
                          )}
                          {p.payment_status === "pending_verification" && (
                            <>
                              <Button variant="outline" size="sm" className="text-rose-600 border-rose-200 hover:bg-rose-50"
                                disabled={!canReject || busyId === p.id}
                                onClick={() => setRejectDialog({ open: true, id: p.id, reason: "" })}
                                data-testid={`pay-reject-btn-${p.id}`}>
                                <XCircle className="w-4 h-4 mr-1" /> Reject Payment
                              </Button>
                              <Button size="sm" className={p.is_renewal ? "bg-indigo-600 hover:bg-indigo-700" : "bg-blue-600 hover:bg-blue-700"}
                                disabled={!canApprove || busyId === p.id}
                                onClick={() => verify(p.id)}
                                data-testid={`pay-verify-btn-${p.id}`}>
                                {p.is_renewal ? (
                                  <><RefreshCcw className="w-4 h-4 mr-1" /> Verify &amp; Extend Access</>
                                ) : (
                                  <><CheckCircle2 className="w-4 h-4 mr-1" /> Verify Payment</>
                                )}
                              </Button>
                            </>
                          )}
                          {!p.is_renewal && (p.payment_status === "verified" || p.payment_status === "pending_verification") && canApprove && (
                            <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700"
                              disabled={busyId === p.id}
                              onClick={() => setConfirmDialog({ open: true, id: p.id })}
                              data-testid={`pay-confirm-btn-${p.id}`}>
                              <ShieldCheck className="w-4 h-4 mr-1" /> Permanently Confirm &amp; Grant Full Access
                            </Button>
                          )}
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </Card>
            )}
          </TabsContent>
        ))}
      </Tabs>

      {/* Reject dialog */}
      <Dialog open={rejectDialog.open} onOpenChange={(o) => setRejectDialog((d) => ({ ...d, open: o }))}>
        <DialogContent data-testid="pay-reject-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-rose-700"><AlertTriangle className="w-5 h-5" /> Reject Payment</DialogTitle>
            <DialogDescription>The student will be asked to upload a valid receipt.</DialogDescription>
          </DialogHeader>
          <div>
            <Label>Reason <span className="text-slate-400 text-xs ml-1">optional ({rejectDialog.reason.length}/300)</span></Label>
            <Textarea value={rejectDialog.reason}
              onChange={(e) => setRejectDialog({ ...rejectDialog, reason: e.target.value.slice(0, 300) })}
              placeholder="e.g. Reference number doesn't match transactions"
              rows={3} data-testid="pay-reject-reason-input" />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setRejectDialog({ open: false, id: null, reason: "" })}>Cancel</Button>
            <Button onClick={reject} className="bg-rose-600 hover:bg-rose-700" data-testid="pay-reject-confirm-btn">Reject Payment</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Permanently confirm dialog */}
      <Dialog open={confirmDialog.open} onOpenChange={(o) => setConfirmDialog((d) => ({ ...d, open: o }))}>
        <DialogContent data-testid="pay-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-emerald-700">
              <ShieldCheck className="w-5 h-5" /> Permanently Confirm Payment
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to permanently confirm this payment and grant full course access to this student?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setConfirmDialog({ open: false, id: null })}>Cancel</Button>
            <Button onClick={confirm} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="pay-confirm-final-btn">
              <ShieldCheck className="w-4 h-4 mr-1" /> Confirm &amp; Grant Full Access
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Receipt viewer */}
      <Dialog open={proofDialog.open} onOpenChange={(o) => setProofDialog((d) => ({ ...d, open: o }))}>
        <DialogContent className="max-w-2xl" data-testid="pay-proof-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><FileText className="w-5 h-5 text-blue-600" /> Payment Receipt</DialogTitle>
            <DialogDescription>{proofDialog.payment?.student_name}</DialogDescription>
          </DialogHeader>
          {proofDialog.loading && (<div className="text-sm text-slate-500 py-6 text-center">Loading…</div>)}
          {proofDialog.data && (
            <div className="space-y-3">
              {proofDialog.data.payment_reference_number && (
                <div className="text-sm"><span className="text-slate-500">Reference: </span><span className="font-mono text-slate-900">{proofDialog.data.payment_reference_number}</span></div>
              )}
              {proofDialog.data.proof_of_payment_data_url ? (
                proofDialog.data.proof_of_payment_mime === "application/pdf" ? (
                  <iframe src={proofDialog.data.proof_of_payment_data_url} title="receipt"
                    className="w-full h-[480px] border rounded-md bg-white" />
                ) : (
                  <img src={proofDialog.data.proof_of_payment_data_url} alt="Receipt"
                    className="max-w-full max-h-[480px] border rounded-md mx-auto bg-white" />
                )
              ) : (
                <div className="text-sm text-slate-500 italic">Student submitted reference number only — no file uploaded.</div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setProofDialog({ open: false, loading: false, data: null, payment: null })}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
